import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Header } from '@/components/layout/Header'
import { FeedContainer } from '@/components/feed/FeedContainer'
import { OnboardingSheet } from '@/components/sheets/OnboardingSheet'
import { UserPostComposer } from '@/components/sheets/UserPostComposer'
import { ProfileSheet } from '@/components/sheets/ProfileSheet'
import { TrackedInsightsSheet } from '@/components/sheets/TrackedInsightsSheet'
import { DocumentManagerSheet } from '@/components/sheets/DocumentManagerSheet'
import { EditProfileModal } from '@/components/modals/EditProfileModal'
import { EditInsightModal } from '@/components/modals/EditInsightModal'
import { AITradingChat } from '@/components/sheets/AITradingChat'
import { BroadcastSheet } from '@/components/sheets/BroadcastSheet'
import { SendSMSSheet } from '@/components/sheets/SendSMSSheet'
import { ImageViewerModal } from '@/components/modals/ImageViewerModal'
import { UserManagementSheet } from '@/components/sheets/UserManagementSheet'
import { AccessGateOverlay } from '@/components/overlays/AccessGateOverlay'
import { PortfolioUpdateComposer } from '@/components/sheets/PortfolioUpdateComposer'
import { ConversationHistorySheet } from '@/components/sheets/ConversationHistorySheet'
import { authRepo, profileRepo } from '@/lib/repositories'
import {
  useInsights,
  useCurrentUser,
  useProfile,
  useCreateInsight,
  useUpdateInsight,
  useDeleteInsight,
  useRealtimeInsights,
  useTrackedIds,
  useTrackInsight,
  useUntrackInsight,
  useCreatePortfolioUpdate,
} from '@/hooks'
import { getStockQuote } from '@/lib/services/stockPrice'
import { type OverlayState } from '@/lib/types/overlay'
import type { Insight } from '@/lib/repositories/interfaces/IInsightsRepository'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

export function FeedPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Filter state (lifted from FeedContainer for server-side filtering)
  const [searchQuery, setSearchQuery] = useState('')
  const [sentimentFilter, setSentimentFilter] = useState<
    'all' | 'bullish' | 'neutral' | 'bearish'
  >('all')

  // Debounced search analytics — fires 800ms after user stops typing
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const captureSearch = useCallback((query: string) => {
    clearTimeout(searchTimerRef.current)
    if (!query) return
    searchTimerRef.current = setTimeout(() => {
      analytics.capture(AnalyticsEvents.FEED_SEARCHED, { query })
    }, 800)
  }, [])

  // TanStack Query hooks — paginated with server-side filters
  const {
    data: insightsData,
    isLoading: insightsLoading,
    error: insightsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInsights({
    search: searchQuery || undefined,
    sentiment: sentimentFilter !== 'all' ? sentimentFilter : undefined,
  })

  // Flatten all loaded pages into a single array
  const insights = useMemo(
    () => insightsData?.pages.flatMap((page) => page.data) ?? [],
    [insightsData]
  )
  const { data: user, isLoading: userLoading } = useCurrentUser()
  const { data: currentProfile } = useProfile(user?.username)
  const createInsightMutation = useCreateInsight()
  const updateInsightMutation = useUpdateInsight()
  const deleteInsightMutation = useDeleteInsight()

  const createPortfolioMutation = useCreatePortfolioUpdate()

  // Tracking hooks
  const { data: trackedIds = [] } = useTrackedIds()
  const trackInsightMutation = useTrackInsight()
  const untrackInsightMutation = useUntrackInsight()

  // Local UI state
  const [overlay, setOverlay] = useState<OverlayState>({ type: 'none' })
  const [showEditProfile, setShowEditProfile] = useState(false)
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false)
  const [editingInsight, setEditingInsight] = useState<Insight | null>(null)

  // Subscribe to realtime updates (invalidates cache)
  useRealtimeInsights({
    enabled: !insightsLoading, // Start after initial load
  })

  // Build track counts map from insights
  const trackCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const insight of insights) {
      counts[insight.id] = insight.trackCount || 0
    }
    return counts
  }, [insights])

  // Check onboarding status
  useEffect(() => {
    async function checkOnboarding() {
      if (user && !hasCheckedOnboarding) {
        try {
          const hasOnboarded = await profileRepo.hasCompletedOnboarding(
            user.username
          )
          if (!hasOnboarded) {
            setOverlay({ type: 'onboarding' })
          }
          setHasCheckedOnboarding(true)
        } catch (error) {
          console.error('Failed to check onboarding:', error)
        }
      }
    }
    checkOnboarding()
  }, [user, hasCheckedOnboarding])

  const handleLogout = async () => {
    try {
      await authRepo.logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const getDisplayName = async (username: string): Promise<string> => {
    try {
      const profile = await profileRepo.getProfile(username)
      return profile?.displayName || username
    } catch {
      return username
    }
  }

  const isAdmin = user?.isAdmin || false
  const isApproved = user?.isApproved ?? false
  const isLoading = insightsLoading || userLoading

  // Admin handlers for edit/delete
  const handleEditInsight = (insight: Insight) => {
    setEditingInsight(insight)
  }

  const handleDeleteInsight = async (insightId: string) => {
    if (!confirm(t('confirmDeleteInsight'))) return

    try {
      await deleteInsightMutation.mutateAsync(insightId)
      toast.success(t('insightDeleted'))
    } catch (error) {
      console.error('Failed to delete insight:', error)
      toast.error(t('failedToDeleteInsight'))
    }
  }

  const handleSaveEditedInsight = async (
    insightId: string,
    data: {
      content: string
      sentiment: 'bullish' | 'bearish' | 'neutral'
      ticker?: string
      tags: string[]
    }
  ) => {
    await updateInsightMutation.mutateAsync({ id: insightId, data })
  }

  // Track toggle handler
  const handleTrackToggle = async (insightId: string, isTracked: boolean) => {
    try {
      if (isTracked) {
        await untrackInsightMutation.mutateAsync(insightId)
        toast.success(t('insightUntracked'))
      } else {
        // Find the insight to get the ticker
        const insight = insights.find((i) => i.id === insightId)
        let priceAtTrack: number | undefined

        // Fetch current price if insight has a ticker
        if (insight?.ticker) {
          const quote = await getStockQuote(insight.ticker)
          priceAtTrack = quote?.currentPrice
        }

        await trackInsightMutation.mutateAsync({ insightId, priceAtTrack })
        toast.success(t('insightTracked'))
      }
    } catch (error) {
      console.error('Failed to toggle track:', error)
      toast.error(t('failedToUpdateTracking'))
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-sovereign-mesh flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center animate-pulse">
            <span
              className="text-black text-3xl"
              style={{
                fontFamily: "'garamond-atf-text', 'EB Garamond', serif",
                fontStyle: 'italic',
                WebkitTextStroke: '1px black',
              }}
            >
              S
            </span>
          </div>
          <div className="text-neutral-400 text-sm">{t('loading')}</div>
        </div>
      </div>
    )
  }

  // Error state
  if (insightsError) {
    return (
      <div className="min-h-screen bg-sovereign-mesh flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="text-red-400 text-lg">{t('failedToLoadInsights')}</div>
          <div className="text-neutral-500 text-sm">
            {insightsError.message}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-sovereign-mesh">
      {/* Subtle background pattern */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50 pointer-events-none" />

      {/* Header Component */}
      <Header
        isAdmin={isAdmin}
        isApproved={isApproved}
        hasPhone={!!currentProfile?.phoneNumber}
        activePage="feed"
        trackedCount={trackedIds.length}
        onBroadcastClick={() => setOverlay({ type: 'broadcast' })}
        onDocumentsClick={() => setOverlay({ type: 'documents' })}
        onUserManagementClick={() => setOverlay({ type: 'user-management' })}
        onSandboxClick={() => navigate('/sandbox')}
        onPortfolioClick={() => navigate('/portfolio')}
        onTrackedClick={() => setOverlay({ type: 'tracked' })}
        onConversationHistoryClick={() =>
          setOverlay({ type: 'conversation-history' })
        }
        onProfileClick={async () => {
          const displayName = await getDisplayName(user?.username || '')
          setOverlay({
            type: 'profile',
            userId: user?.id || '',
            username: user?.username || '',
            displayName,
          })
        }}
        onLogoutClick={handleLogout}
      />

      {/* Feed Container Component — blurred for non-approved users */}
      <div
        className={!isApproved ? 'blur-md pointer-events-none select-none' : ''}
      >
        <FeedContainer
          username={user?.username || 'user'}
          displayName={currentProfile?.displayName}
          isAdmin={isAdmin}
          insights={insights}
          trackedIds={trackedIds}
          trackCounts={trackCounts}
          isTrackLoading={
            trackInsightMutation.isPending || untrackInsightMutation.isPending
          }
          searchQuery={searchQuery}
          onSearchChange={(query: string) => {
            setSearchQuery(query)
            captureSearch(query)
          }}
          sentimentFilter={sentimentFilter}
          onSentimentChange={(sentiment: 'all' | 'bullish' | 'neutral' | 'bearish') => {
            setSentimentFilter(sentiment)
            if (sentiment !== 'all') analytics.capture(AnalyticsEvents.FEED_FILTERED, { sentiment })
          }}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onInsightClick={(insightId, ticker, sentiment, content) =>
            setOverlay({
              type: 'ai-chat',
              insightId,
              ticker,
              sentiment,
              content,
            })
          }
          onImageClick={(imageUrl: string) =>
            setOverlay({ type: 'image-viewer', imageUrl })
          }
          onUserClick={(userId, username, displayName) =>
            setOverlay({
              type: 'profile',
              userId,
              username,
              displayName,
            })
          }
          onGeneralChatClick={() => setOverlay({ type: 'general-ai-chat' })}
          onEditInsight={handleEditInsight}
          onDeleteInsight={handleDeleteInsight}
          onTrackToggle={handleTrackToggle}
        />
      </div>

      {/* Access Gate for non-approved users */}
      {!isApproved && <AccessGateOverlay />}

      {/* Overlays (Sheets & Modals) */}
      <OnboardingSheet
        open={overlay.type === 'onboarding'}
        onClose={() => setOverlay({ type: 'none' })}
        username={user?.username || 'user'}
      />

      <UserPostComposer
        open={overlay.type === 'composer'}
        onClose={() => setOverlay({ type: 'none' })}
        onSubmit={async (newInsight) => {
          try {
            await createInsightMutation.mutateAsync({
              ...newInsight,
              userId: user?.id || 'unknown',
              username: user?.username || 'anonymous',
              isAdmin: user?.isAdmin || false,
            })
            // Cache invalidation happens automatically via mutation's onSuccess
          } catch (error) {
            console.error('Failed to create insight:', error)
          }
        }}
      />

      <ImageViewerModal
        open={overlay.type === 'image-viewer'}
        imageUrl={overlay.type === 'image-viewer' ? overlay.imageUrl : ''}
        onClose={() => setOverlay({ type: 'none' })}
      />

      <ProfileSheet
        open={overlay.type === 'profile'}
        onClose={() => setOverlay({ type: 'none' })}
        userId={overlay.type === 'profile' ? overlay.userId : ''}
        username={overlay.type === 'profile' ? overlay.username : ''}
        displayName={
          overlay.type === 'profile' ? overlay.displayName : undefined
        }
        bio={
          overlay.type === 'profile' && overlay.username === user?.username
            ? currentProfile?.bio
            : undefined
        }
        hasPhone={!!currentProfile?.phoneNumber}
        insights={insights}
        isCurrentUser={
          overlay.type === 'profile' && user?.id === overlay.userId
        }
        onEditProfile={() => {
          setShowEditProfile(true)
        }}
      />

      <EditProfileModal
        open={showEditProfile}
        onClose={() => setShowEditProfile(false)}
        currentUsername={user?.username || ''}
        currentDisplayName={currentProfile?.displayName || ''}
        currentBio={currentProfile?.bio || ''}
        currentPhoneNumber={currentProfile?.phoneNumber || ''}
        currentBirthdate={currentProfile?.birthdate || ''}
        currentShippingAddress={currentProfile?.shippingAddress}
        currentEmail={user?.email || ''}
        usernameUpdatedAt={currentProfile?.usernameUpdatedAt}
        onSave={() => {
          // Profile cache is invalidated by useUpdateProfile mutation
          // If username changed, we need to refetch current user
        }}
      />

      <AITradingChat
        open={
          overlay.type === 'ai-chat' ||
          overlay.type === 'general-ai-chat' ||
          overlay.type === 'resume-ai-chat'
        }
        onClose={() => setOverlay({ type: 'none' })}
        insightId={
          overlay.type === 'ai-chat'
            ? overlay.insightId
            : overlay.type === 'resume-ai-chat'
              ? overlay.insightId
              : undefined
        }
        ticker={
          overlay.type === 'ai-chat'
            ? overlay.ticker
            : overlay.type === 'resume-ai-chat'
              ? overlay.ticker
              : undefined
        }
        sentiment={overlay.type === 'ai-chat' ? overlay.sentiment : undefined}
        content={overlay.type === 'ai-chat' ? overlay.content : undefined}
        resumeConversationId={
          overlay.type === 'resume-ai-chat' ? overlay.conversationId : undefined
        }
      />

      <ConversationHistorySheet
        open={overlay.type === 'conversation-history'}
        onClose={() => setOverlay({ type: 'none' })}
        isAdmin={isAdmin}
        onSelectConversation={(conv) => {
          if (conv.conversationType === 'sandbox') {
            navigate('/sandbox', {
              state: { conversationId: conv.id },
            })
          } else {
            setOverlay({
              type: 'resume-ai-chat',
              conversationId: conv.id,
              insightId: conv.insightId,
              ticker: conv.ticker,
            })
          }
        }}
      />

      <PortfolioUpdateComposer
        open={overlay.type === 'portfolio-composer'}
        onClose={() => setOverlay({ type: 'none' })}
        onSubmit={async (data) => {
          await createPortfolioMutation.mutateAsync({
            ...data,
            userId: user?.id || '',
          })
          navigate('/portfolio')
        }}
      />

      <BroadcastSheet
        open={overlay.type === 'broadcast'}
        onClose={() => setOverlay({ type: 'none' })}
        onPostInsight={() => setOverlay({ type: 'composer' })}
        onSendSMS={() => setOverlay({ type: 'send-sms' })}
        onPostPortfolioUpdate={() => setOverlay({ type: 'portfolio-composer' })}
      />

      <SendSMSSheet
        open={overlay.type === 'send-sms'}
        onClose={() => setOverlay({ type: 'none' })}
        onBack={() => setOverlay({ type: 'broadcast' })}
      />

      <TrackedInsightsSheet
        open={overlay.type === 'tracked'}
        onClose={() => setOverlay({ type: 'none' })}
        onAskAI={(insightId, ticker, sentiment, content) => {
          setOverlay({
            type: 'ai-chat',
            insightId,
            ticker,
            sentiment,
            content,
          })
        }}
      />

      <DocumentManagerSheet
        open={overlay.type === 'documents'}
        onClose={() => setOverlay({ type: 'none' })}
      />

      <UserManagementSheet
        open={overlay.type === 'user-management'}
        onClose={() => setOverlay({ type: 'none' })}
      />

      {/* Admin Edit Insight Modal */}
      <EditInsightModal
        open={editingInsight !== null}
        onClose={() => setEditingInsight(null)}
        insight={editingInsight}
        onSave={handleSaveEditedInsight}
      />
    </div>
  )
}
